/**
 * VideoLogTab — superadmin only. Pick which users get their app session
 * recorded; browse recordings by day; each user-day is ONE file with its
 * length in minutes, playable in-app and downloadable.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, Film, Flag, Loader2, Minus, Pause, Play, Plus, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import "rrweb/dist/style.css";

const todayISO = () => {
  const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 10);
};
const shiftISO = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const pretty = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
const fmtMin = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}m` : `${Math.round(m)} min`);

// Same idle-compression rule as the backend (recordlog.py): event-free gaps
// longer than 10s collapse to a 1.5s beat, so the replay shows ACTIVE time —
// a 6-hour clocked day with an hour of real use plays in about an hour, not six.
const IDLE_GAP_MS = 10_000;
const IDLE_SHOWN_MS = 1_500;
const compressEvents = (events) => {
  const out = [];
  let offset = 0;
  let prev = null;
  const sorted = [...events].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  for (const e of sorted) {
    const ts = e.timestamp || 0;
    if (prev !== null && ts - prev > IDLE_GAP_MS) offset += (ts - prev) - IDLE_SHOWN_MS;
    prev = ts;
    out.push({ ...e, timestamp: ts - offset });
  }
  return out;
};

function ReplayModal({ userId, name, date, bookmarks: initialBms, onBookmarks, onClose }) {
  const stageRef = useRef(null);
  const repRef = useRef(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [curMs, setCurMs] = useState(0);
  const [totalMs, setTotalMs] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [bms, setBms] = useState(initialBms || []);
  const [showBms, setShowBms] = useState(false);

  const applyBms = (next) => {
    setBms(next);
    onBookmarks?.(next);
  };

  const addBookmark = async () => {
    const note = window.prompt(`Flag this moment (${fmtT(curMs)}) — optional note:`);
    if (note === null) return;
    try {
      const { data } = await api.post("/record/bookmark", { t_ms: Math.round(curMs), note }, { params: { user_id: userId, date } });
      applyBms([...bms, data].sort((a, b) => a.t_ms - b.t_ms));
      toast.success(`Bookmarked at ${fmtT(data.t_ms)}`);
    } catch {
      toast.error("Could not save the bookmark");
    }
  };

  const removeBookmark = async (bmId) => {
    try {
      await api.delete("/record/bookmark", { params: { user_id: userId, date, bm_id: bmId } });
      applyBms(bms.filter((b) => b.bm_id !== bmId));
    } catch {
      toast.error("Could not delete the bookmark");
    }
  };

  // the Settings dialog's overlay/content intercept pointer events above this
  // fullscreen layer — mute them while the replay is open, restore on close.
  // The flag also tells SettingsDialog not to treat taps inside this portal
  // as "outside clicks" that close the settings window.
  useEffect(() => {
    window.__videoLogOpen = true;
    const els = document.querySelectorAll("[data-dialog-overlay], [data-dialog-content]");
    const saved = [...els].map((el) => [el, el.style.pointerEvents]);
    els.forEach((el) => { el.style.pointerEvents = "none"; });
    return () => {
      window.__videoLogOpen = false;
      saved.forEach(([el, v]) => { el.style.pointerEvents = v; });
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let fitTries = 0;
    let fitTimer = null;
    let progTimer = null;

    const fit = () => {
      const stage = stageRef.current;
      const player = stage?.querySelector(".replayer-wrapper");
      const frame = stage?.querySelector("iframe");
      if (!stage || !player || !frame || !frame.clientWidth || !frame.clientHeight) {
        if (++fitTries < 20 && alive) fitTimer = setTimeout(fit, 300);
        return;
      }
      // scale the recorded viewport into the stage and CENTER the scaled
      // result — transforms don't shrink the layout box, so position the
      // wrapper by its post-scale size or wide desktop recordings crop
      const pw = frame.clientWidth;
      const ph = frame.clientHeight;
      const scale = Math.min(stage.clientWidth / pw, stage.clientHeight / ph, 1);
      stage.style.position = "relative";
      player.style.transform = `scale(${scale})`;
      player.style.transformOrigin = "top left";
      player.style.position = "absolute";
      player.style.left = `${Math.max(0, (stage.clientWidth - pw * scale) / 2)}px`;
      player.style.top = `${Math.max(0, (stage.clientHeight - ph * scale) / 2)}px`;
    };

    (async () => {
      try {
        const { data } = await api.get("/record/replay", { params: { user_id: userId, date } });
        if (!alive || !stageRef.current || !data.events?.length) {
          if (alive) { setErr("No recording data for that day."); setLoading(false); }
          return;
        }
        const { Replayer } = await import("rrweb");
        // feed gap-compressed events: idle time plays as a 1.5s beat, the
        // total matches the duration shown in the day list
        const replayer = new Replayer(compressEvents(data.events), { root: stageRef.current, showController: false });
        repRef.current = replayer;
        setTotalMs(replayer.getMetaData().totalTime);
        replayer.play();
        fit();
        window.addEventListener("resize", fit);
        progTimer = setInterval(() => {
          if (!alive || !repRef.current) return;
          const st = scrubRef.current;
          if (st.timer || st.busy || st.pending !== null) return;
          setCurMs(repRef.current.getCurrentTime());
        }, 300);
        if (alive) setLoading(false);
      } catch {
        if (alive) { setErr("Could not load the recording."); setLoading(false); }
      }
    })();
    return () => {
      alive = false;
      clearTimeout(fitTimer);
      clearInterval(progTimer);
      window.removeEventListener("resize", fit);
      try { repRef.current?.pause(); } catch { /* noop */ }
    };
  }, [userId, date]);

  const togglePlay = () => {
    const r = repRef.current;
    if (!r) return;
    if (playing) {
      r.pause();
      setPlaying(false);
    } else {
      r.play(curMs >= totalMs - 100 ? 0 : curMs);
      setPlaying(true);
    }
  };

  // Seeking makes rrweb rebuild the page state from the start of the
  // recording, which is expensive. While the slider is dragged we only move
  // the handle; the real seek runs once, when the drag ends (or after a
  // short pause), and never overlaps another seek.
  const scrubRef = useRef({ timer: null, pending: null, busy: false });
  const applySeek = (ms) => {
    const r = repRef.current;
    const st = scrubRef.current;
    if (!r) return;
    if (st.busy) { st.pending = ms; return; }
    st.busy = true;
    requestAnimationFrame(() => {
      try {
        if (playing) r.play(ms);
        else r.pause(ms);
      } catch { /* a bad frame must not freeze the UI */ }
      st.busy = false;
      if (st.pending !== null) { const next = st.pending; st.pending = null; applySeek(next); }
    });
  };
  const seek = (ms, immediate = true) => {
    setCurMs(ms);
    const st = scrubRef.current;
    clearTimeout(st.timer);
    st.timer = null;
    if (immediate) applySeek(ms);
    else st.timer = setTimeout(() => { st.timer = null; applySeek(ms); }, 250);
  };

  const changeSpeed = (s) => {
    setSpeed(s);
    repRef.current?.setConfig({ speed: s });
  };

  const fmtT = (ms) => {
    const s = Math.floor((ms || 0) / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  return createPortal(
    <div className="pointer-events-auto fixed inset-0 z-[200] flex flex-col bg-[#0d0d0d]" data-testid="videolog-replay-modal">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <p className="text-sm font-semibold text-white">{name} — {pretty(date)}</p>
        <button onClick={onClose} data-testid="videolog-replay-close" aria-label="Close replay"
          className="rounded-md border border-white/15 p-2 text-white/60 hover:bg-white/10 hover:text-white">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="relative flex-1 overflow-hidden bg-white">
        <div ref={stageRef} className="h-full w-full overflow-hidden" />
        {loading && !err && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0d0d0d]">
            <Loader2 className="h-6 w-6 animate-spin text-[#8fa4f0]" />
          </div>
        )}
        {err && <div className="absolute inset-0 flex items-center justify-center bg-[#0d0d0d] text-xs text-white/55">{err}</div>}
      </div>
      {/* player controls */}
      <div className="relative flex items-center gap-3 border-t border-white/10 bg-[#0d0d0d] px-4 py-3" data-testid="videolog-controls">
        <button onClick={togglePlay} data-testid="videolog-toggle-play" aria-label={playing ? "Pause" : "Play"}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1a3fd1] text-white hover:bg-[#1633a3]">
          {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
        </button>
        <span className="shrink-0 font-mono text-[11px] text-white/60 tabular-nums" data-testid="videolog-time">
          {fmtT(curMs)} / {fmtT(totalMs)}
        </span>
        <div className="relative flex-1">
          <input
            type="range"
            min={0}
            max={Math.max(1, totalMs)}
            value={Math.min(curMs, totalMs)}
            onChange={(e) => seek(Number(e.target.value), false)}
            onMouseUp={(e) => seek(Number(e.target.value))}
            onTouchEnd={(e) => seek(Number(e.target.value))}
            onKeyUp={(e) => seek(Number(e.target.value))}
            data-testid="videolog-seek"
            className="h-1.5 w-full cursor-pointer accent-[#1a3fd1]"
          />
          {totalMs > 0 && bms.map((b) => (
            <button
              key={b.bm_id}
              onClick={() => seek(b.t_ms)}
              data-testid={`videolog-marker-${b.bm_id}`}
              title={`${fmtT(b.t_ms)}${b.note ? ` — ${b.note}` : ""}`}
              className="absolute -top-2.5 h-0 w-0 -translate-x-1/2 border-x-[5px] border-t-[7px] border-x-transparent border-t-amber-400 hover:border-t-amber-300"
              style={{ left: `${Math.min(100, (b.t_ms / totalMs) * 100)}%` }}
            />
          ))}
        </div>
        <button onClick={addBookmark} data-testid="videolog-bookmark-add" aria-label="Bookmark this moment"
          title="Bookmark this moment"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-amber-400/50 bg-amber-400/10 text-amber-300 hover:bg-amber-400/25">
          <Flag className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => setShowBms((s) => !s)} data-testid="videolog-bookmarks-toggle"
          aria-label="Show bookmarks"
          className={`flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2 font-mono text-[11px] ${bms.length ? "border-amber-400/50 text-amber-300" : "border-white/15 text-white/40"} hover:bg-white/5`}>
          <Flag className="h-3 w-3" /> {bms.length}
        </button>
        <select
          value={speed}
          onChange={(e) => changeSpeed(Number(e.target.value))}
          data-testid="videolog-speed"
          className="shrink-0 rounded-md border border-white/15 bg-[#0d0d0d] px-2 py-1 font-mono text-[11px] text-white/70"
        >
          {[1, 2, 4, 8].map((s) => <option key={s} value={s}>{s}x</option>)}
        </select>
        {showBms && (
          <div data-testid="videolog-bookmarks-panel"
            className="absolute bottom-full right-4 mb-2 w-64 overflow-hidden rounded-lg border border-white/15 bg-[#141414] shadow-xl">
            <p className="border-b border-white/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/40">Bookmarks</p>
            {bms.length === 0 ? (
              <p className="px-3 py-3 text-[11px] text-white/35">No bookmarks yet — hit the flag while playing.</p>
            ) : (
              <div className="max-h-56 overflow-y-auto">
                {bms.map((b) => (
                  <div key={b.bm_id} data-testid={`videolog-bookmark-item-${b.bm_id}`}
                    className="flex items-center gap-2 border-b border-white/5 px-3 py-2 last:border-0">
                    <button onClick={() => { seek(b.t_ms); setShowBms(false); }}
                      className="min-w-0 flex-1 text-left">
                      <span className="font-mono text-[11px] font-semibold text-amber-300">{fmtT(b.t_ms)}</span>
                      {b.note && <span className="ml-2 truncate text-[11px] text-white/60">{b.note}</span>}
                    </button>
                    <button onClick={() => removeBookmark(b.bm_id)} data-testid={`videolog-bookmark-delete-${b.bm_id}`}
                      aria-label="Delete bookmark"
                      className="shrink-0 text-white/30 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default function VideoLogTab() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [days, setDays] = useState(null);
  const [saving, setSaving] = useState(false);
  const [replay, setReplay] = useState(null); // {userId, name}
  const [videoBusy, setVideoBusy] = useState(null); // user_id being rendered

  const loadUsers = useCallback(async () => {
    try {
      const [{ data: all }, { data: cfg }] = await Promise.all([
        api.get("/users"), api.get("/record/settings"),
      ]);
      setUsers(all || []);
      setSelected(new Set(cfg.user_ids || []));
    } catch { /* guarded server-side too */ }
  }, []);

  const loadDays = useCallback(async (d) => {
    try {
      const { data } = await api.get("/record/days", { params: { date: d } });
      setDays(data);
    } catch {
      setDays(null);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadDays(date); }, [date, loadDays]);

  const toggleUser = async (uid) => {
    const next = new Set(selected);
    next.has(uid) ? next.delete(uid) : next.add(uid);
    setSelected(next);
    setSaving(true);
    try {
      await api.put("/record/settings", { user_ids: [...next] });
      toast.success(next.has(uid) ? "Recording turned on" : "Recording turned off");
    } catch {
      toast.error("Could not save the recording list");
    } finally {
      setSaving(false);
    }
  };

  const download = async (r) => {
    try {
      const res = await api.get("/record/file", { params: { user_id: r.user_id, date }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `videolog-${(r.name || "user").replace(/\s+/g, "_")}-${date}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const downloadVideo = async (r) => {
    setVideoBusy(r.user_id);
    const toastId = toast.loading(`Rendering ${r.name}'s day to video — can take a few minutes…`, { duration: Infinity });
    try {
      const res = await api.get("/record/video", {
        params: { user_id: r.user_id, date },
        responseType: "blob",
        timeout: 600000,
      });
      const cd = res.headers["content-disposition"] || "";
      const fn = (cd.match(/filename="?([^";]+)/) || [])[1]
        || `videolog-${(r.name || "user").replace(/\s+/g, "_")}-${date}.mp4`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "video/mp4" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fn;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Video downloaded", { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video render failed", { id: toastId });
    } finally {
      setVideoBusy(null);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.name}'s recording for ${pretty(date)}? This cannot be undone.`)) return;
    try {
      await api.delete("/record/day", { params: { user_id: r.user_id, date } });
      toast.success("Recording deleted");
      loadDays(date);
    } catch {
      toast.error("Delete failed");
    }
  };

  const recordings = days?.recordings || [];

  return (
    <div className="space-y-5" data-testid="videolog-tab">
      {/* who gets recorded — collapsed by default */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            Record these users
            <span className="ml-2 text-white/25" data-testid="videolog-selected-count">
              {selected.size ? `${selected.size} selected` : "none selected"}
            </span>
          </p>
          <button
            type="button"
            onClick={() => setPickerOpen((o) => !o)}
            data-testid="videolog-picker-toggle"
            aria-label={pickerOpen ? "Collapse user list" : "Expand user list"}
            className="flex h-6 w-6 items-center justify-center rounded border border-white/15 text-white/60 hover:border-white/30 hover:text-white"
          >
            {pickerOpen ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          </button>
        </div>
        {pickerOpen && (
          <>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {users.map((u) => (
                <label key={u.user_id}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2"
                  data-testid={`videolog-user-${u.user_id}`}>
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-[#1a3fd1]"
                    checked={selected.has(u.user_id)}
                    disabled={saving}
                    onChange={() => toggleUser(u.user_id)}
                  />
                  <span className="min-w-0 flex-1 truncate text-xs font-medium text-white/85">{u.name || u.email}</span>
                  <span className="font-mono text-[9px] uppercase text-white/30">{u.role}</span>
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-[10px] text-white/30">Recording starts within ~15 seconds of them using the app and covers everything they do in it.</p>
          </>
        )}
      </div>

      {/* dated recordings */}
      <div>
        <div className="mb-2 flex items-center gap-2">
          <button onClick={() => setDate((d) => shiftISO(d, -1))} aria-label="Previous day"
            data-testid="videolog-prev-day"
            className="rounded-md border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25 hover:text-white">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <input type="date" value={date} max={todayISO()} onChange={(e) => e.target.value && setDate(e.target.value)}
            data-testid="videolog-date"
            className="rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-sm text-white [color-scheme:dark] focus:border-[#4f6fe8] focus:outline-none" />
          <button onClick={() => setDate((d) => shiftISO(d, 1))} disabled={days ? date >= todayISO() : false}
            aria-label="Next day" data-testid="videolog-next-day"
            className="rounded-md border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25 hover:text-white disabled:opacity-30">
            <ChevronRight className="h-4 w-4" />
          </button>
          <span className="text-sm font-semibold text-white/80">{pretty(date)}</span>
        </div>

        {days === null ? (
          <p className="py-6 text-center text-xs text-white/40"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></p>
        ) : recordings.length === 0 ? (
          <p className="rounded-lg border border-dashed border-white/10 py-6 text-center text-xs text-white/35" data-testid="videolog-empty">
            No recordings for this day.
          </p>
        ) : (
          <div className="space-y-1.5">
            {recordings.map((r) => (
              <div key={r.user_id}
                className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2"
                data-testid={`videolog-row-${r.user_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-white/90">{r.name}</p>
                  <p className="font-mono text-[10px] text-white/40">{fmtMin(r.minutes)} · {(r.bytes / 1048576).toFixed(1)} MB</p>
                </div>
                {r.bookmarks?.length > 0 && (
                  <span data-testid={`videolog-bookmark-count-${r.user_id}`}
                    title={`${r.bookmarks.length} bookmark${r.bookmarks.length > 1 ? "s" : ""}`}
                    className="flex items-center gap-1 rounded border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 font-mono text-[10px] text-amber-300">
                    <Flag className="h-2.5 w-2.5" /> {r.bookmarks.length}
                  </span>
                )}
                <button onClick={() => setReplay({ userId: r.user_id, name: r.name })}
                  data-testid={`videolog-play-${r.user_id}`}
                  className="flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-[#1633a3]">
                  <Play className="h-3.5 w-3.5" /> Play
                </button>
                <button onClick={() => downloadVideo(r)} disabled={videoBusy === r.user_id}
                  data-testid={`videolog-video-${r.user_id}`}
                  title="Render and download as video (.webm) — can take a few minutes"
                  className="flex items-center gap-1.5 rounded-md border border-[#1a3fd1]/60 bg-[#1a3fd1]/10 px-3 py-1.5 text-[11px] font-semibold text-[#8fa4f0] hover:bg-[#1a3fd1]/25 disabled:opacity-50">
                  {videoBusy === r.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />}
                  {videoBusy === r.user_id ? "Rendering…" : "Video"}
                </button>
                <button onClick={() => download(r)}
                  data-testid={`videolog-download-${r.user_id}`}
                  title="Download the raw recording data (.json)"
                  className="rounded-md border border-white/15 p-1.5 text-white/60 hover:text-white">
                  <Download className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => remove(r)}
                  data-testid={`videolog-delete-${r.user_id}`}
                  title="Delete this recording"
                  className="rounded-md border border-white/15 p-1.5 text-white/40 hover:border-red-500/40 hover:text-red-400">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {replay && (
        <ReplayModal userId={replay.userId} name={replay.name} date={date}
          bookmarks={recordings.find((r) => r.user_id === replay.userId)?.bookmarks || []}
          onBookmarks={(next) => setDays((d) => d && ({
            ...d,
            recordings: d.recordings.map((r) => r.user_id === replay.userId ? { ...r, bookmarks: next } : r),
          }))}
          onClose={() => setReplay(null)} />
      )}
    </div>
  );
}
